import React, { useState, useEffect } from 'react';
import { 
  useStripe, 
  useElements, 
  PaymentElement,
  Elements 
} from '@stripe/react-stripe-js';
import { useStripeContext } from '../../contexts/StripeContext';
import { paymentService } from '../../services/paymentService';
import { CreditCard, Lock, AlertCircle, Loader2, Check } from 'lucide-react';

// Inner form component that uses Stripe hooks
const StripePaymentFormInner = ({
  amount,
  planName,
  interval,
  onSuccess,
  onError,
  userEmail,
  userName,
  userId
}) => {
  const stripe = useStripe();
  const elements = useElements();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [paymentData, setPaymentData] = useState(null);

  // Handle payment element ready state
  useEffect(() => {
    if (!elements) return;

    const paymentElement = elements?.getElement(PaymentElement);
    if (!paymentElement) return;

    paymentElement?.on('ready', () => {
      console.log('Payment element ready');
      setIsReady(true);
    });

    paymentElement?.on('change', (event) => {
      if (event?.error) {
        setError(event?.error?.message);
      } else {
        setError(null);
      }
    });

    return () => {
      paymentElement?.off('ready');
      paymentElement?.off('change');
    };
  }, [elements]);

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!stripe || !elements) {
      setError('Payment system is not ready. Please wait a moment and try again.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Confirm payment with Stripe
      const { error: stripeError, paymentIntent } = await stripe?.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location?.origin}/order-confirmation`,
        },
        redirect: 'if_required'
      });

      if (stripeError) {
        // Payment failed
        throw new Error(stripeError.message || 'Payment failed');
      }

      if (paymentIntent && paymentIntent?.status === 'succeeded') {
        // Payment succeeded - confirm on backend
        const confirmResult = await paymentService?.confirmPayment(paymentIntent?.id);
        
        setPaymentSuccess(true);
        setPaymentData({
          paymentIntentId: paymentIntent?.id,
          orderNumber: confirmResult?.data?.order?.order_number,
          warning: confirmResult?.warning
        });

        // Call onSuccess callback after a brief delay to show success animation
        setTimeout(() => {
          if (onSuccess) {
            onSuccess({
              paymentIntentId: paymentIntent?.id,
              orderNumber: confirmResult?.data?.order?.order_number,
              warning: confirmResult?.warning
            });
          }
        }, 2000);
      } else {
        throw new Error('Payment processing incomplete. Please try again.');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err?.message || 'Payment failed. Please try again.');
      if (onError) {
        onError(err);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Show success state
  if (paymentSuccess) {
    return (
      <div className="text-center py-8">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
          <Check className="w-10 h-10 text-green-600" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h3>
        <p className="text-gray-600 mb-4">
          Your subscription to {planName} has been activated.
        </p>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 inline-block">
          <p className="text-lg font-semibold text-green-800">
            ${amount?.toFixed(2)} Paid
          </p>
          {paymentData?.orderNumber && (
            <p className="text-sm text-green-600 mt-1">
              Order #{paymentData?.orderNumber}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Show payment form
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <CreditCard className="w-4 h-4 inline mr-2" />
          Card Information
        </label>
        <div className="border border-gray-300 rounded-lg p-4 focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-transparent transition-all">
          <PaymentElement 
            options={{
              layout: 'tabs'
            }}
          />
        </div>
      </div>
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-800 font-medium">Payment Error</p>
            <p className="text-sm text-red-600 mt-1">{error}</p>
            <button
              type="button"
              onClick={() => setError(null)}
              className="mt-2 text-xs text-red-700 hover:text-red-800 underline"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
      <div className="bg-gray-50 rounded-lg p-4 flex items-center">
        <Lock className="w-5 h-5 text-gray-500 mr-3" />
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-700">Secure Payment</p>
          <p className="text-xs text-gray-500">Your payment information is encrypted and secure</p>
        </div>
      </div>
      <button
        type="submit"
        disabled={!stripe || !isReady || isProcessing}
        className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold py-4 px-6 rounded-lg hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl text-lg flex items-center justify-center"
      >
        {isProcessing ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Processing Payment...
          </>
        ) : (
          <>
            <CreditCard className="w-5 h-5 mr-2" />
            Pay ${amount?.toFixed(2)} - Complete {interval === 'month' ? 'Monthly' : 'Annual'} Subscription
          </>
        )}
      </button>
      <p className="text-xs text-center text-gray-500">
        By confirming your subscription, you agree to our Terms of Service and Privacy Policy.
        {interval === 'month' && ' Your subscription will automatically renew monthly.'}
        {interval === 'year' && ' Your subscription will automatically renew annually.'}
      </p>
    </form>
  );
};

// Wrapper component that provides Elements context
const StripePaymentForm = (props) => {
  const { stripePromise } = useStripeContext();
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initError, setInitError] = useState(null);

  // Create payment intent to get client secret for Elements
  useEffect(() => {
    const initPayment = async () => {
      try {
        setLoading(true);
        setInitError(null);

        const orderData = {
          planName: props?.planName,
          interval: props?.interval,
          total: props?.amount,
          items: [{
            name: props?.planName,
            description: `${props?.interval}ly subscription`,
            price: props?.amount,
            quantity: 1
          }],
          subtotal: props?.amount,
          tax: 0,
          currency: 'usd'
        };

        const customerInfo = {
          email: props?.userEmail,
          userName: props?.userName,
          userId: props?.userId,
          firstName: props?.userName ? props?.userName?.split(' ')?.[0] : '',
          lastName: props?.userName ? props?.userName?.split(' ')?.[1] : '',
          billing: {
            address_line_1: 'N/A',
            city: 'N/A',
            state: 'N/A',
            postal_code: '00000',
            country: 'US'
          }
        };

        console.log('Creating payment intent...', { orderData, customerInfo });
        const result = await paymentService?.createPaymentIntent(orderData, customerInfo);
        console.log('Payment intent created:', result);
        
        setClientSecret(result?.clientSecret);
      } catch (error) {
        console.error('Failed to initialize payment:', error);
        setInitError(error?.message || 'Failed to initialize payment system');
        if (props?.onError) {
          props?.onError(error);
        }
      } finally {
        setLoading(false);
      }
    };

    if (props?.userEmail && props?.userName && props?.amount) {
      initPayment();
    }
  }, [props?.amount, props?.planName, props?.interval, props?.userEmail, props?.userName, props?.userId]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center">
          <Loader2 className="w-5 h-5 text-blue-600 mr-3 animate-spin" />
          <div>
            <p className="text-sm text-blue-800 font-medium">Preparing payment system...</p>
            <p className="text-xs text-blue-600 mt-1">This will only take a moment</p>
          </div>
        </div>
      </div>
    );
  }

  if (initError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-red-800 font-medium">Initialization Error</p>
            <p className="text-sm text-red-600 mt-1">{initError}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-sm text-yellow-800">Waiting for payment initialization...</p>
      </div>
    );
  }

  const options = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#6366f1',
        colorBackground: '#ffffff',
        colorText: '#1f2937',
        colorDanger: '#ef4444',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        spacingUnit: '4px',
        borderRadius: '8px',
      }
    }
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <StripePaymentFormInner {...props} />
    </Elements>
  );
};

export default StripePaymentForm;